import groupBy from 'lodash/groupBy';
import reduce from 'lodash/reduce';

const clamp = (value, min, max) => {
  if (!Number.isFinite(value)) {
    return min;
  }

  if (min > max) {
    return min;
  }

  return Math.min(Math.max(value, min), max);
};

/**
 * hasParentWithClassName searches class name from parent elements of given target
 * @param {Node} target - element whose parent might contain given class.
 * @param {String} className - class name string to be found
 */
export const hasParentWithClassName = (target, className) => {
  if (!target || !className) {
    return false;
  }

  return [...document.querySelectorAll(`.${className}`)].some(
    el => el === target || el.contains(target)
  );
};

/**
 * Listings array grouped by geolocation
 * @param {Array} mapListings - listings to be grouped on map
 * @return {Object} - Object where coordinate pair is the key to different listings
 */
export const groupedByCoordinates = mapListings => {
  return groupBy(mapListings, l => {
    const g = l.attributes.geolocation;
    return `${g.lat}-${g.lng}`;
  });
};

/**
 * Listings (in location based object literal) is mapped to array
 * @param {Object} mapListings - listings to be grouped on map
 * @return {Array} - An array where items are arrays of listings
 *   (They are arrays containing all the listings in that location)
 */
export const reducedToArray = mapListings => {
  return reduce(mapListings, (acc, listing) => acc.concat([listing]), []);
};

export const listingIdsFromSelection = selection => {
  const listingsArray = Array.isArray(selection) ? selection : selection ? [selection] : [];
  return listingsArray.map(listing => listing?.id?.uuid).filter(Boolean);
};

export const hasSameListingSelection = (currentSelection, nextSelection) => {
  const currentIds = listingIdsFromSelection(currentSelection);
  const nextIds = listingIdsFromSelection(nextSelection);

  if (currentIds.length !== nextIds.length) {
    return false;
  }

  return currentIds.every((id, index) => id === nextIds[index]);
};

export const syncListingSelection = (selection, listings) => {
  const listingsArray = Array.isArray(selection) ? selection : selection ? [selection] : [];

  if (!listingsArray.length) {
    return null;
  }

  const listingsById = (listings || []).reduce((acc, listing) => {
    const id = listing?.id?.uuid;
    return id ? { ...acc, [id]: listing } : acc;
  }, {});
  const syncedSelection = listingsArray
    .map(listing => listingsById[listing?.id?.uuid])
    .filter(Boolean);

  if (!syncedSelection.length) {
    return null;
  }

  return Array.isArray(selection) ? syncedSelection : syncedSelection[0];
};

export const createListingsSignature = listings =>
  (listings || [])
    .map(listing => listing?.id?.uuid)
    .filter(Boolean)
    .join(',');

export const getMapReattachmentToken = refreshToken => {
  return String(refreshToken || '').split(':')[0];
};

/**
 * Calculate a mobile popup position that stays near the marker while remaining inside
 * the visible map viewport.
 *
 * @param {Object} args
 * @param {number} args.containerWidth
 * @param {number} args.containerHeight
 * @param {number} args.cardWidth
 * @param {number} args.cardHeight
 * @param {number} args.pointX
 * @param {number} args.pointY
 * @param {number} [args.gap]
 * @param {number} [args.margin]
 * @returns {{left: number, top: number, placement: 'above' | 'below'} | null}
 */
export const getMobileInfoCardLayout = ({
  containerWidth,
  containerHeight,
  cardWidth,
  cardHeight,
  pointX,
  pointY,
  gap = 16,
  margin = 12,
}) => {
  const values = [containerWidth, containerHeight, cardWidth, cardHeight, pointX, pointY];
  if (!values.every(Number.isFinite)) {
    return null;
  }

  const boundedCardWidth = Math.min(cardWidth, Math.max(containerWidth - margin * 2, 0));
  const boundedCardHeight = Math.min(cardHeight, Math.max(containerHeight - margin * 2, 0));
  const minLeft = margin;
  const maxLeft = Math.max(margin, containerWidth - boundedCardWidth - margin);
  const minTop = margin;
  const maxTop = Math.max(margin, containerHeight - boundedCardHeight - margin);
  const aboveTop = pointY - boundedCardHeight - gap;
  const belowTop = pointY + gap;
  const canPlaceAbove = aboveTop >= minTop;
  const canPlaceBelow = belowTop + boundedCardHeight <= containerHeight - margin;

  let placement = 'above';
  let top = aboveTop;

  if (!canPlaceAbove && canPlaceBelow) {
    placement = 'below';
    top = belowTop;
  } else if (!canPlaceAbove && !canPlaceBelow) {
    const aboveOverflow = minTop - aboveTop;
    const belowOverflow = belowTop + boundedCardHeight - (containerHeight - margin);
    placement = aboveOverflow <= belowOverflow ? 'above' : 'below';
    top = placement === 'above' ? aboveTop : belowTop;
  }

  return {
    left: clamp(pointX - boundedCardWidth / 2, minLeft, maxLeft),
    top: clamp(top, minTop, maxTop),
    placement,
  };
};
